import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import Layout from "@/components/layout/Layout";
import { Skeleton } from "@/components/ui/skeleton";
import TopProgressBar from "@/components/TopProgressBar";

// Lazy-loaded pages
const Index = lazy(() => import("./pages/Index"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Login = lazy(() => import("./pages/Login"));
const Signup = lazy(() => import("./pages/Signup"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Calculators = lazy(() => import("./pages/Calculators"));
const CalculatorPage = lazy(() => import("./pages/CalculatorPage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const LawUpdates = lazy(() => import("./pages/LawUpdates"));
const EmploymentLawCalculators = lazy(() => import("./pages/EmploymentLawCalculators"));
const PropertyLawCalculators = lazy(() => import("./pages/PropertyLawCalculators"));
const FinancialLawCalculators = lazy(() => import("./pages/FinancialLawCalculators"));
const FamilyLawCalculators = lazy(() => import("./pages/FamilyLawCalculators"));
const InjuryLawCalculators = lazy(() => import("./pages/InjuryLawCalculators"));
const ConsumerLawCalculators = lazy(() => import("./pages/ConsumerLawCalculators"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const Disclaimer = lazy(() => import("./pages/Disclaimer"));
const RefundPolicy = lazy(() => import("./pages/RefundPolicy"));
const ShippingAndDelivery = lazy(() => import("./pages/ShippingAndDelivery"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const DataRights = lazy(() => import("./pages/DataRights"));
const AffiliateDisclosure = lazy(() => import("./pages/AffiliateDisclosure"));
const DashboardLayout = lazy(() => import("./components/dashboard/DashboardLayout"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const DashboardHistory = lazy(() => import("./pages/dashboard/History"));
const DashboardReports = lazy(() => import("./pages/dashboard/Reports"));
const DashboardBilling = lazy(() => import("./pages/dashboard/Billing"));
const DashboardProfile = lazy(() => import("./pages/dashboard/Profile"));
const DashboardSettings = lazy(() => import("./pages/dashboard/Settings"));
const AdminLayout = lazy(() => import("./components/admin/AdminLayout"));
const AdminOverview = lazy(() => import("./pages/admin/Overview"));
const AdminUsers = lazy(() => import("./pages/admin/Users"));
const AdminAnalytics = lazy(() => import("./pages/admin/Analytics"));
const AdminRevenue = lazy(() => import("./pages/admin/Revenue"));
const AdminPricing = lazy(() => import("./pages/admin/Pricing"));
const AdminPromos = lazy(() => import("./pages/admin/Promos"));
const AdminPrompts = lazy(() => import("./pages/admin/Prompts"));
const AdminLawUpdates = lazy(() => import("./pages/admin/AdminLawUpdates"));
const AdminCalculators = lazy(() => import("./pages/admin/AdminCalculators"));
const AdminBlog = lazy(() => import("./pages/admin/AdminBlog"));
const AdminBlogEditor = lazy(() => import("./pages/admin/AdminBlogEditor"));

const PageFallback = () => (
  <div className="min-h-screen flex items-center justify-center">
    <TopProgressBar />
    <div className="space-y-4 w-full max-w-md px-4">
      <Skeleton className="h-8 w-3/4" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-5/6" />
      <Skeleton className="h-32 w-full" />
    </div>
  </div>
);

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <Suspense fallback={<PageFallback />}>
            <Routes>
              {/* Admin — dark layout, admin guard */}
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<AdminOverview />} />
                <Route path="users" element={<AdminUsers />} />
                <Route path="analytics" element={<AdminAnalytics />} />
                <Route path="revenue" element={<AdminRevenue />} />
                <Route path="pricing" element={<AdminPricing />} />
                <Route path="promos" element={<AdminPromos />} />
                <Route path="prompts" element={<AdminPrompts />} />
                <Route path="law-updates" element={<AdminLawUpdates />} />
                <Route path="calculators" element={<AdminCalculators />} />
                <Route path="blog" element={<AdminBlog />} />
                <Route path="blog/new" element={<AdminBlogEditor />} />
                <Route path="blog/:id/edit" element={<AdminBlogEditor />} />
              </Route>

              {/* Dashboard — own layout, no global Navbar/Footer */}
              <Route path="/dashboard" element={<DashboardLayout />}>
                <Route index element={<Dashboard />} />
                <Route path="history" element={<DashboardHistory />} />
                <Route path="reports" element={<DashboardReports />} />
                <Route path="billing" element={<DashboardBilling />} />
                <Route path="profile" element={<DashboardProfile />} />
                <Route path="settings" element={<DashboardSettings />} />
                <Route path="calculators" element={<Calculators />} />
                <Route path="calculators/:calculatorId" element={<CalculatorPage />} />
              </Route>

              {/* Public pages with global Layout */}
              <Route element={<Layout />}>
                <Route path="/" element={<Index />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/employment-law-calculators" element={<EmploymentLawCalculators />} />
                <Route path="/property-law-calculators" element={<PropertyLawCalculators />} />
                <Route path="/financial-law-calculators" element={<FinancialLawCalculators />} />
                <Route path="/family-law-calculators" element={<FamilyLawCalculators />} />
                <Route path="/injury-law-calculators" element={<InjuryLawCalculators />} />
                <Route path="/consumer-law-calculators" element={<ConsumerLawCalculators />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/law-updates" element={<LawUpdates />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms-of-service" element={<TermsOfService />} />
                <Route path="/disclaimer" element={<Disclaimer />} />
                <Route path="/refund-policy" element={<RefundPolicy />} />
                <Route path="/shipping-and-delivery" element={<ShippingAndDelivery />} />
                <Route path="/cookie-policy" element={<CookiePolicy />} />
                <Route path="/data-rights" element={<DataRights />} />
                <Route path="/affiliate-disclosure" element={<AffiliateDisclosure />} />
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/auth/callback" element={<AuthCallback />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="*" element={<NotFound />} />
              </Route>
            </Routes>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
